import {PayloadAction, createSlice} from '@reduxjs/toolkit';
import {setDebugApiUrl, storeUserData} from '../user';

/**
 * The staff second factor (backend "MFA elevation").
 *
 * A superuser trades a TOTP or recovery code for a short-lived token that the
 * backend requires, as the `X-Staff-Elevation` header, on Login As and on the
 * `user/search` / `user/presets` endpoints. The token is a credential: it is
 * held in memory only -- BOTH apps blacklist this slice from redux-persist,
 * and their persistConfig tests assert it never reaches storage -- so a reload
 * or an app restart asks for a fresh code.
 */
export type StaffElevationState = {
  token: string | null;
  /** Epoch seconds, as every API datetime. */
  expiresAt: number | null;
  /**
   * The backend answered a request with `staff_elevation_required`: the Debug
   * screens show the code prompt. Only a 403 sets this -- never the mere
   * absence of a token -- so nothing is asked of staff before the backend's
   * rollout flag is switched on.
   */
  required: boolean;
};

const initialState: StaffElevationState = {
  token: null,
  expiresAt: null,
  required: false,
};

/** The 403 `detail.code` the backend answers a gated request with. */
export const STAFF_ELEVATION_REQUIRED = 'staff_elevation_required';

/** The 403 `detail.code` for a Login As whose target is a staff account. */
export const LOGIN_AS_TARGET_FORBIDDEN = 'login_as_target_forbidden';

const staffElevationSlice = createSlice({
  name: 'staffElevation',
  initialState,
  reducers: {
    setStaffElevation: (
      _state,
      {payload}: PayloadAction<{token: string; expiresAt: number}>,
    ) => ({
      token: payload.token,
      expiresAt: payload.expiresAt,
      required: false,
    }),
    clearStaffElevation: (state) => {
      state.token = null;
      state.expiresAt = null;
    },
    /**
     * `rejectedToken` is the token the refused request carried (null if
     * none). A request sent before the admin elevated can come back 403 after
     * the new token was stored; that answer says nothing about the new token,
     * so it is ignored rather than allowed to throw away the code just typed.
     */
    staffElevationRequired: (
      state,
      {payload}: PayloadAction<{rejectedToken: string | null}>,
    ) => {
      if (state.token !== payload.rejectedToken) {
        return state;
      }
      return {token: null, expiresAt: null, required: true};
    },
  },
  extraReducers: (builder) => {
    builder
      // A token only accompanies user details on a fresh sign-in
      // (storeLoginDetails): a new session must not inherit an elevation.
      // Plain user/me syncs -- including the one Login As makes, which stores
      // the TARGET's details -- carry none and leave it alone.
      .addCase(storeUserData, (state, {payload}) =>
        payload.token ? initialState : state,
      )
      // A token minted by one backend means nothing to another.
      .addCase(setDebugApiUrl, () => initialState)
      // Both apps' root reducers already reset every non-preference slice on
      // LOGOUT; handled here too so the guarantee does not depend on them.
      .addMatcher(
        (action) => action.type === 'LOGOUT',
        () => initialState,
      );
  },
});

export const {setStaffElevation, clearStaffElevation, staffElevationRequired} =
  staffElevationSlice.actions;

export default staffElevationSlice.reducer;
